<template>
  <div class="flex h-[calc(100vh-60px)] bg-white relative">
    <!-- Left Column: Patient List -->
    <div :class="['left', { collapsed: isLeftColumnCollapsed }]" class="transition-all duration-500 ease-in-out">
      <RoomList
        v-show="!isLeftColumnCollapsed"
        ref="roomListRef"
        @clickRoom="currentRoomId = $event; showNotificationTabs = false"
        @creatingRoom="isCreatingRoom = $event"
        @roomListLoaded="onRoomListLoaded"
        @toggleNotifications="showNotificationTabs = !showNotificationTabs"
      />
    </div>

    <!-- Collapse/Expand Button -->
    <div
      class="collapse-btn-wrapper"
      :style="{ left: isLeftColumnCollapsed ? 'calc(40px - 16px)' : 'calc(334px - 14px)' }"
    >
      <button
        class="collapse-expand-button"
        @click="toggleLeftColumn"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" :style="{ transform: isLeftColumnCollapsed ? 'rotate(180deg)' : 'none' }">
          <path d="M17.59 6L19 7.41L14.42 12L19 16.59L17.59 18L11.59 12L17.59 6Z" fill="#374957"/>
          <path d="M11 6L12.41 7.41L7.83 12L12.41 16.59L11 18L5 12L11 6Z" fill="#374957"/>
        </svg>
      </button>
    </div>

    <!-- Middle and Right Columns -->
    <div :class="['right', { shifted: isLeftColumnCollapsed, 'right--no-scroll': showNotificationTabs }]">
      <!-- Middle Column: Chat Workspace -->
      <div class="flex flex-col bg-white border-r border-gray-200 transition-all duration-500 ease-in-out" :class="isLeftColumnCollapsed ? 'w-full' : 'w-full'">
        <Loading
          v-if="isCreatingRoom"
          :loading="true"
          loadingText="Creating room..."
        />
        <div v-else-if="currentRoomId" class="h-full">
          <!-- Patient Chat -->
          <div v-if="!isSupportRoom" class="h-full border-b border-gray-200">
            <ChatWindow ref="chatWindowRef" :roomId="currentRoomId" @go-to-chat="handleGoToChatFromTag"/>
          </div>
          <!-- Internal Support Chat -->
          <div v-else class="h-full">
            <InternalChat :support-room="supportRoomData" />
          </div>
        </div>
        <div v-else class="flex-1 overflow-y-auto">
          <div class="flex items-center justify-center h-full text-gray-500">
            Select a patient to start chatting
          </div>
        </div>
      </div>

      <!-- Right Column: Peg Board (hidden for support room) -->
      <div v-if="!isSupportRoom" class="peg-board transition-all duration-500 ease-in-out" :class="isLeftColumnCollapsed ? 'w-[520px]' : 'w-[480px]'">
        <Pegboard
          ref="pegboardRef"
          :patient="currentPatientId"
          :roomId="currentRoomId"
          :isLeftColumnCollapsed="isLeftColumnCollapsed"
          @add-new="handlePegboardAddNew"
          @edit="handlePegboardEdit"
          @edit-note="handlePegboardEditNote"
          @edit-contact="handlePegboardEditContact"
          @edit-appointment="handlePegboardEditAppointment"
          @edit-task="handlePegboardEditTask"
          @discontinue="handlePegboardDiscontinue"
          @reconcile="handlePegboardReconcile"
        />
      </div>

    </div>

    <!-- Notification Tabs Floating Overlay (fixed position) -->
    <div v-if="showNotificationTabs" class="notification-backdrop" :style="{ left: isLeftColumnCollapsed ? '24px' : '334px' }" @click="showNotificationTabs = false" />
    <transition name="notification-slide">
      <NotificationTabs
        v-if="showNotificationTabs"
        class="notification-tabs-overlay"
        :style="{ left: isLeftColumnCollapsed ? '74px' : '360px' }"
        @close="showNotificationTabs = false"
        @jump-to-conversation="handleJumpToConversation"
      />
    </transition>

  </div>
</template>

<script>
import ChatWindow from "../MyPatient-demo/index.vue"
import Pegboard from './Pegboard.vue'
import RoomList from "./RoomList.vue";
import NotificationTabs from "./notification-tabs/NotificationTabs.vue";
import InternalChat from './InternalChat.vue';
import Loading from '@/NewLibrary/Loading/Loading.vue';
import { mapGetters, mapState } from 'vuex';
import wsService from "@/services/websocketService.js";

export default {
  name: 'MyPatients',
  components: {
    RoomList,
    ChatWindow,
    Pegboard,
    NotificationTabs,
    InternalChat,
    Loading,
  },

  data() {
    return {
      currentRoomId: null,
      isCreatingRoom: false,
      isLeftColumnCollapsed: false,
      showNotificationTabs: false,
      windowWidth: window.innerWidth,
      isWebSocketAuthenticated: false,
      isRoomListLoaded: false,
    };
  },

  computed: {
    ...mapGetters('myPatients/rooms', ['getPatientByRoomId']),
    ...mapGetters('login', ['rcUserId']),
    ...mapState('login', ['support_room']),
    currentPatientId() {
      const p = this.getPatientByRoomId(this.currentRoomId);
      return p?.patientBasicInfo?.patientId || null;
    },
    isSupportRoom() {
      return this.currentRoomId && this.support_room?.room_id === this.currentRoomId;
    },
    supportRoomData() {
      return this.support_room || null;
    },
  },

  watch: {
    currentRoomId(newRoomId, oldRoomId) {
      if (newRoomId === oldRoomId) return;
      
      // Unsubscribe from old room
      if (oldRoomId) {
        this.unsubscribeFromRoom(oldRoomId);
      }
      
      // Subscribe to new room if WebSocket is authenticated
      if (newRoomId && this.isWebSocketAuthenticated) {
        this.subscribeToRoom(newRoomId);
      }
    },
    
    '$route.query.roomId': {
      handler(newRoomId) {
        if (newRoomId && newRoomId !== this.currentRoomId) {
          this.handleRoomIdFromRoute();
        }
      },
      immediate: false
    }
  },

  mounted() {
    this.checkWindowWidth();
    window.addEventListener('resize', this.handleResize);
    
    // Initialize WebSocket connection when My Patients page loads
    this.initializeWebSocket();
    
    // Handle roomId from route query params for deep linking
    this.handleRoomIdFromRoute();
  },

  activated() {
    // This hook fires every time the component is reactivated from keep-alive
    // Handle roomId from route query params when navigating back to this page
    this.handleRoomIdFromRoute();
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    // Disconnect WebSocket when leaving My Patients page
    wsService.disconnect();
  },

  methods: {
    initializeWebSocket() {
      const ws_url = this.$store.state.login.ws_url;
      const token = sessionStorage.getItem('token');
      
      if (!ws_url || !token) {
        console.warn('⚠️ WebSocket URL or token not available, skipping WebSocket initialization');
        return;
      }

      try {
        wsService.connect(
          ws_url,
          token,
          (data) => {
            this.handleWebSocketMessage(data);
          },
          () => {
            console.log('✅ WebSocket authenticated at parent level');
            this.isWebSocketAuthenticated = true;
            
            // Subscribe to notify-user stream for subscription-changed events
            this.subscribeNotifyUser();
            
            // Subscribe to current room if one is selected
            if (this.currentRoomId) {
              this.subscribeToRoom(this.currentRoomId);
            }
          }
        );
        console.log('🔌 WebSocket connected at My Patients page level');
      } catch (error) {
        console.error('❌ Failed to initialize WebSocket:', error);
      }
    },

    handleWebSocketMessage(data) {
      try {
        // Handle room-message events (messages in current room)
        if (data.event === 'room-message') {
          const message = data.message;
          if (message) {
            const roomId = message.rid;
            
            // Update room list last message for ANY room
            this.$store.dispatch('myPatients/rooms/updateRoomLastMessage', {
              roomId: roomId,
              message: message
            });
            
            // Get support and internal room IDs
            const supportRoom = this.$store.state.login?.support_room || null;
            const internalRoom = this.$store.state.login?.internal_room || null;
            const supportRoomId = supportRoom?.room_id;
            const internalRoomId = internalRoom?.room_id;
            
            // If message is for current room OR support/internal room, add to message store
            if (roomId === this.currentRoomId || roomId === supportRoomId || roomId === internalRoomId) {
              if (message.u._id !== this.rcUserId) {
                // Message from others
                this.$store.dispatch('myPatients/message/addMessage', {
                  roomId: roomId,
                  message: message
                });
              } else {
                // Message from current user - check for duplicates
                const existingSentMessage = this.$store.getters['myPatients/message/getSentMessage'](message.rid, message._id);
                if (!existingSentMessage) {
                  this.$store.dispatch('myPatients/message/addSentMessage', {
                    roomId: message.rid,
                    messageId: message._id,
                    message: message
                  });
                  this.$store.dispatch('myPatients/message/addMessage', {
                    roomId: message.rid,
                    message: message
                  });
                }
              }
            }
          }
        }

        // Handle room list updates (new message in any room)
        else if (data.event === 'rooms-changed') {
          const payload = data.room || data.payload;
          if (payload?.last_message) {
            const roomId = payload.last_message.rid || payload.room_id;
            this.$store.commit('myPatients/rooms/UPDATE_TOP_ROOM_ID', roomId);
            this.$store.dispatch('myPatients/rooms/updateRoomLastMessage', {
              roomId: roomId,
              message: payload.last_message
            });
          }
        }

        // Handle unread count updates
        else if (data.event === 'subscription-changed') {
          const payload = data.payload;
          if (payload) {
            // Don't increment unread count if this is the current active room
            if (payload.room_id !== this.currentRoomId) {
              this.$store.dispatch('myPatients/rooms/updateCurrentChangeUnreadRoom', {
                roomId: payload.room_id,
                unreadCount: payload.unread_count
              });
              this.$store.dispatch('myPatients/rooms/setUnreadCount', {
                roomId: payload.room_id,
                unreadCount: payload.unread_count || 0
              });
              
              // Also update lastSeenMap for support room
              const supportRoom = this.$store.state.login?.support_room;
              if (supportRoom && payload.room_id === supportRoom.room_id) {
                const existingData = this.$store.getters['myPatients/rooms/getLastSeenByRoomId'](payload.room_id);
                this.$store.dispatch('myPatients/rooms/updateRoomLastSeen', {
                  roomId: payload.room_id,
                  lastSeenData: {
                    ...existingData,
                    unread: payload.unread_count || 0
                  }
                });
              }
            }
          }
        }

        // Handle Guardian Agent events
        else if (['guardian_alert', 'guardian_message', 'guardian_resolved'].includes(data.event)) {
          this.$store.dispatch('notifications/handleGuardianWebSocketEvent', {
            event: data.event,
            payload: data.payload || data,
          });
        }
      } catch (error) {
        console.error('❌ Error handling WebSocket message at parent level:', error);
      }
    },

    subscribeNotifyUser() {
      console.log('📡 Subscribing to notify-user stream');
      wsService.send({
        event: 'subscribe',
        stream: 'notify-user'
      });
    },

    subscribeToRoom(roomId) {
      if (!roomId) return;
      console.log('📡 Subscribing to room:', roomId);
      wsService.send({
        event: 'subscribe',
        stream: 'room-messages',
        room_id: roomId
      });
    },

    unsubscribeFromRoom(roomId) {
      if (!roomId) return;
      console.log('🗑️ Unsubscribing from room:', roomId);
      wsService.send({
        event: 'unsubscribe',
        stream: 'room-messages',
        room_id: roomId
      });
    },

    handlePegboardAddNew({ entityType }) {
      if (entityType === 'allergy') {
        this.$refs.chatWindowRef?.openAllergyFormDialog();
      } else if (entityType === 'health') {
        this.$refs.chatWindowRef?.openHealthInfoDialog();
      } else {
        this.$refs.chatWindowRef?.openMedicationFormDialog();
      }
    },

    handlePegboardEdit({ data, entityType }) {
      if (entityType === 'allergy') {
        this.$refs.chatWindowRef?.handleEditAllergy(data);
      } else {
        this.$refs.chatWindowRef?.handleEditMedication(data);
      }
    },

    handlePegboardDiscontinue({ data, entityType }) {
      if (entityType === 'medication') {
        this.$refs.chatWindowRef?.handleDiscontinueMedication(data);
      }
    },

    handlePegboardReconcile() {
      this.$refs.chatWindowRef?.handleReconcile();
    },

    handlePegboardEditNote(noteId) {
      this.$refs.chatWindowRef?.handleEditNote(noteId);
    },

    handlePegboardEditContact() {
      this.$refs.chatWindowRef?.handleEditContact();
    },
    handlePegboardEditAppointment(appointment) {
      this.$refs.chatWindowRef?.handleEditAppointment(appointment);
    },
    handlePegboardEditTask(task) {
      this.$refs.chatWindowRef?.handleEditTask(task);
    },
    // ===== Persistence helpers for newly-created (pending) patients =====
    readPendingPatients() {
      try {
        const raw = localStorage.getItem('pendingPatients');
        const arr = JSON.parse(raw || '[]');
        return Array.isArray(arr) ? arr : [];
      } catch (_) {
        return [];
      }
    },
    writePendingPatients(list) {
      try {
        localStorage.setItem('pendingPatients', JSON.stringify(list || []));
      } catch (_) {
        // ignore
      }
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
      this.checkWindowWidth();
    },

    checkWindowWidth() {
      if (this.windowWidth < 1500) {
        this.isLeftColumnCollapsed = true;
      } else if (this.windowWidth >= 1500 && this.isLeftColumnCollapsed) {
        this.isLeftColumnCollapsed = false;
      }
    },

    toggleLeftColumn() {
      this.isLeftColumnCollapsed = !this.isLeftColumnCollapsed;
    },

    handleRoomIdFromRoute() {
      const roomId = this.$route.query.roomId;
      if (!roomId) return;

      // Only proceed if room list is fully loaded
      if (!this.isRoomListLoaded) {
        console.log('⏳ Waiting for room list to finish loading before setting roomId...');
        return;
      }

      // Check if room exists in the loaded room list
      const room = this.$store.getters['myPatients/rooms/getPatientByRoomId'](roomId);

      if (room) {
        console.log('✅ Room found, setting currentRoomId:', roomId);
        this.currentRoomId = roomId;
        this.$store.dispatch('myPatients/rooms/setCurrentRoomId', roomId);

        // Clear roomId from URL query params for cleaner URL
        this.$router.replace({
          path: this.$route.path,
          query: { ...this.$route.query, roomId: undefined }
        });
      } else {
        console.warn('⚠️ Room not found in loaded room list:', roomId);
      }
    },

    onRoomListLoaded() {
      console.log('✅ Room list fully loaded');
      this.isRoomListLoaded = true;

      // Now that room list is loaded, check if we need to set a roomId from route
      const hasRoomIdInRoute = this.$route.query.roomId;
      
      if (hasRoomIdInRoute) {
        // If roomId is in route, use that
        this.handleRoomIdFromRoute();
      } else if (!this.currentRoomId) {
        // If no roomId in route and no room currently selected, auto-select first room
        const roomList = this.$store.state.myPatients.rooms.roomList;
        if (roomList && roomList.length > 0) {
          const firstRoom = roomList[0];
          console.log('🎯 Auto-selecting first room:', firstRoom.roomId);
          this.currentRoomId = firstRoom.roomId;
          this.$store.dispatch('myPatients/rooms/setCurrentRoomId', firstRoom.roomId);
        }
      }
    },

    async handleGoToChatFromTag({ patientId }) {
      // 1. Try to find the patient in the current room list
      const roomList = this.$store.state.myPatients?.rooms?.roomList || [];
      const room = roomList.find(
        r => String(r.patientBasicInfo?.patientId) === String(patientId)
      );

      if (room) {
        // Patient found in room list — delegate to RoomList.selectPatient
        // which handles both cases: room exists (navigate) or no room yet (create & navigate)
        this.$refs.roomListRef?.selectPatient(room.patientBasicInfo);
        return;
      }

      // 2. Patient not in room list — fetch by ID, then delegate to RoomList
      try {
        const { getPatientByIdApi } = await import('@/api/patientList');
        const patientData = await getPatientByIdApi(patientId);
        if (patientData) {
          const patientInfo = this.$refs.roomListRef?.mapBackendPatient(patientData);
          if (patientInfo) {
            this.$refs.roomListRef?.selectPatient(patientInfo);
          }
        }
      } catch (e) {
        console.error('Failed to fetch patient for go-to-chat:', patientId, e);
      }
    },

    handleJumpToConversation({ patientId, noteId }) {
      // 1. Close the notification panel
      this.showNotificationTabs = false;

      // 2. Find the room for this patient
      const roomList = this.$store.state.myPatients?.rooms?.roomList || [];
      const room = roomList.find(
        r => String(r.patientBasicInfo?.patientId) === String(patientId)
      );

      if (!room?.roomId) {
        console.warn('No room found for patient:', patientId);
        return;
      }

      const targetRoomId = room.roomId;
      const needsSwitch = this.currentRoomId !== targetRoomId;

      // 3. Switch to that patient's room and open internal notes
      if (needsSwitch) {
        this.currentRoomId = targetRoomId;
        this.$store.dispatch('myPatients/rooms/setCurrentRoomId', targetRoomId);
        this.$store.dispatch('myPatients/rooms/setUnreadCount', { roomId: targetRoomId, unreadCount: 0 });
        // Wait for ChatWindow to mount/update with the new room, then open notes
        this.$nextTick(() => {
          setTimeout(() => {
            this.$refs.chatWindowRef?.handleOpenComments(noteId);
          }, 500);
        });
      } else {
        // Already on the right room, just open the dialog
        this.$refs.chatWindowRef?.handleOpenComments(noteId);
      }
    },
  },
};
</script>

<style scoped>
.collapse-btn-wrapper {
  position: fixed;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  width: 28px;
  z-index: 10;
  display: flex;
  align-items: center;
  pointer-events: none;
  transition: all 0.5s ease-in-out;
}

.collapse-expand-button {
  pointer-events: auto;
  display: flex;
  padding: 24px 2px;
  align-items: center;
  gap: 12px;
  border-radius: 4px;
  background: #F2F2F2;
  border: none;
  cursor: pointer;
}

.left {
  background: #F9FBFD;
  display: flex;
  flex-direction: column;
  padding: 0;
  border-radius: 0 10px 10px 0;
  box-shadow: 0px 2px 15px 0px #4A4A4A40;
  margin-right: 14px;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 334px;
  transition: all 0.5s ease-in-out;
  height: calc(100vh - var(--nav-height));
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-color: transparent transparent;
}

.left.collapsed {
  transform: translateX(-310px);
}

.left:hover {
  scrollbar-color: #c1c1c1 #f1f1f1;
}

.peg-board {
  background-color: #004879;
  padding: 32px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
  max-width: 400px;
}

.right {
  margin-left: 350px;
  transition: all 0.5s ease-in-out;
  display: flex;
  flex-grow: 1;
  min-width: 0;
  height: calc(100vh - var(--nav-height));
  overflow: hidden;
  position: relative;
  z-index: 1;
}

.right.shifted {
  margin-left: 64px;
}

.right.right--no-scroll {
  overflow: hidden;
}

/* Notification backdrop */
.notification-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  background: rgba(51, 51, 51, 0.24);
  z-index: 19;
}

/* Notification Tabs floating overlay */
.notification-tabs-overlay {
  position: fixed;
  top: 80px; /* 60px navbar + 20px gap */
  right: 20px;
  bottom: 20px; /* always leave a gap at the bottom */
  height: auto !important; /* override notification-tabs height:100% which would be 100vh on fixed elements */
  z-index: 20;
  border-radius: 10px;
  transition: left 0.5s ease-in-out;
  overflow: hidden;
}

/* Slide-in transition */
.notification-slide-enter-active {
  transition: all 0.3s ease-out;
}
.notification-slide-leave-active {
  transition: all 0.2s ease-in;
}
.notification-slide-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}
.notification-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
